import { useEffect, useState } from 'react'
import Sheet from './Sheet.jsx'
import Links from './Links.jsx'
import { addTask, addProject, dismissReminder } from '../lib/notes.js'
import {
  resolveTarget, rescheduleReminder, setReminderNote, deleteReminder,
  laterToday, tomorrow, toLocalInput,
} from '../lib/reminderActions.js'

function when(iso) {
  const d = new Date(iso)
  const now = new Date()
  const time = d.toLocaleTimeString('en-PH', { hour: 'numeric', minute: '2-digit' })
  const day = d.toDateString() === now.toDateString()
    ? 'Today'
    : d.toLocaleDateString('en-PH', { day: 'numeric', month: 'short' })
  return day + ', ' + time
}

/**
 * One reminder, opened.
 *
 * Every action closes the loop where he is standing. The thing this points at
 * can be finished from here, because having to go and find it in another tab
 * is what made reminders feel like labels rather than reminders.
 */
export default function ReminderSheet({ reminder, onClose, onChanged, toast }) {
  const [target, setTarget] = useState(undefined)   // undefined = still looking
  const [note, setNote] = useState(reminder.note ?? '')
  const [at, setAt] = useState(toLocalInput(reminder.remind_at))
  const [busy, setBusy] = useState(false)
  const [picking, setPicking] = useState(false)

  useEffect(() => {
    let alive = true
    resolveTarget(reminder)
      .then((t) => { if (alive) setTarget(t) })
      .catch(() => { if (alive) setTarget(null) })
    return () => { alive = false }
  }, [reminder])

  const overdue = new Date(reminder.remind_at) < new Date()

  /* Every action shares this: do it, tell the list to reload, close. A failure
     leaves the sheet open with the reason, because closing on a failure is how
     you end up believing something happened that did not. */
  async function run(fn, failure) {
    if (busy) return
    setBusy(true)
    try {
      await fn()
      onChanged?.()
      onClose()
    } catch (e) {
      toast(e.message || failure)
      setBusy(false)
    }
  }

  return (
    <Sheet title="Reminder" onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <p style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 600 }}>{reminder.body}</p>
        <span className="lbl" style={{ color: overdue ? 'var(--late)' : undefined }}>
          {when(reminder.remind_at)}{overdue ? ' · overdue' : ''}
        </span>
      </div>

      {target ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="rows">
            <div className="row">
              <div className="nm">
                <b>{target.name}</b>
                <span className="lbl">{target.label}{target.detail ? ' · ' + target.detail : ''}</span>
              </div>
              {target.action ? (
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => run(
                    async () => {
                      await target.action.run(target.row)
                      await dismissReminder(reminder.id)
                    },
                    'Could not do that.',
                  )}
                >
                  {target.action.label}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <p className="lbl" style={{ margin: '0 2px 6px' }}>Reschedule</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <button type="button" className="btn ghost" disabled={busy}
          onClick={() => run(() => rescheduleReminder(reminder.id, laterToday()), 'Could not move it.')}>
          Later today
        </button>
        <button type="button" className="btn ghost" disabled={busy}
          onClick={() => run(() => rescheduleReminder(reminder.id, tomorrow()), 'Could not move it.')}>
          Tomorrow
        </button>
        <button type="button" className="btn ghost" disabled={busy}
          onClick={() => setPicking((p) => !p)}>
          Pick a time
        </button>
      </div>

      {picking ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            style={{ flex: 1 }}
          />
          <button type="button" className="btn" disabled={busy || !at}
            onClick={() => run(() => rescheduleReminder(reminder.id, at), 'Could not move it.')}>
            Move
          </button>
        </div>
      ) : <div style={{ height: 8 }} />}

      <p className="lbl" style={{ margin: '0 2px 6px' }}>Note</p>
      <textarea
        rows={2}
        value={note}
        placeholder="Anything worth keeping with this"
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          if ((reminder.note ?? '') === note) return
          setReminderNote(reminder.id, note)
            .then(() => onChanged?.())
            .catch((e) => toast(e.message || 'Could not save that note.'))
        }}
        style={{ width: '100%', marginBottom: 16 }}
      />

      <p className="lbl" style={{ margin: '0 2px 6px' }}>Turn into</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button type="button" className="btn ghost" disabled={busy}
          onClick={() => run(
            async () => {
              await addTask({ body: reminder.body, dueOn: reminder.remind_at.slice(0, 10) })
              await dismissReminder(reminder.id)
            },
            'Could not make a task.',
          )}>
          Task
        </button>
        <button type="button" className="btn ghost" disabled={busy}
          onClick={() => run(
            async () => {
              await addProject({ title: reminder.body })
              await dismissReminder(reminder.id)
            },
            'Could not make a project.',
          )}>
          Project
        </button>
      </div>

      <Links target={{ type: 'reminder', id: reminder.id }} toast={toast} />

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <button type="button" className="btn" style={{ flex: 1 }} disabled={busy}
          onClick={() => run(() => dismissReminder(reminder.id), 'Could not close it.')}>
          Done
        </button>
        <button type="button" className="btn ghost" disabled={busy}
          onClick={() => run(() => deleteReminder(reminder.id), 'Could not delete it.')}>
          Delete
        </button>
      </div>
    </Sheet>
  )
}
